import { t } from "@lingui/core/macro";
import { intersperse } from "@regrello/core-utils";
import type React from "react";

export interface RegrelloListPhraseProps<T> {
  /** The items to display in the list. */
  items: T[];

  /** A function invoked to render each item in the list. */
  renderItem: (item: T) => React.ReactNode;

  /**
   * Whether to put "and" before the final item, instead of just a comma.
   *
   * @default false
   */
  showAsConjunctionList?: boolean;
}

/**
 * Displays a generic list of things as a textual phrase (handling comma-separation, "and", etc.).
 */
export function RegrelloListPhrase<T>({
  items,
  renderItem,
  showAsConjunctionList = false,
}: RegrelloListPhraseProps<T>) {
  const itemElements = items.map(renderItem);

  if (showAsConjunctionList && itemElements.length === 2) {
    return (
      <>
        {intersperse(itemElements, {
          type: "callback",
          // (clewis): Should come up with a better i18n strategy here.
          callback: (_leftElement, leftIndex) => <span key={`separator-${leftIndex}`}> {t`and`} </span>,
        })}
      </>
    );
  }

  return (
    <>
      {intersperse(itemElements, {
        type: "callback",
        // (clewis): Display name is not necessary here.
        callback: (_leftElement, leftIndex) => {
          if (showAsConjunctionList && itemElements.length >= 3 && leftIndex === itemElements.length - 2) {
            return <span key={`separator-${leftIndex}`}>, {t`and`} </span>;
          }
          return <span key={`separator-${leftIndex}`}>, </span>;
        },
      })}
    </>
  );
}
